import { useEffect, useRef, useState } from "react";
import { C } from "../../shared/theme";
import { categoryLabel } from "../../shared/categories";
import { selectStyle } from "../../shared/textStyles";
import { EditorFrame, fieldLabel, DeleteButton } from "../../shared/EditorFrame";
import { Segmented } from "../../shared/Segmented";
import { growToFit } from "../../shared/growToFit";
import { goalChoices, startingGoalId } from "../tasks/goalOrder";
import { GoalSelect } from "../tasks/GoalSelect";
import { itemText, guessReduce, makeHabit, HABIT_FREQUENCIES } from "./compassLists";

// Adding or editing one item on a Mindset, Gratitude, Habits or Temptations page: its words, its category when
// the kind is grouped, and for a habit whether you're building it or cutting back. `kind` is a COMPASS_KINDS entry;
// `item` is the stored item, or null for a new one. `categories` is given for grouped kinds only.
// A habit also has how often it's meant to happen and the goal it serves, both optional: `goals` are the user's
// goals and `initialGoalId` presets a new habit's (the Goal page's + Add). isTaken(text, categoryId) says whether
// the words repeat one already there. onSave(item, categoryId) and onDelete() change the user's compass.
export function CompassItemEditor({ kind, item = null, initialCategoryId = "", initialGoalId = "", categories = null, goals = [], isTaken, onSave, onDelete, onClose }) {
  const isNew = !item;
  const [text, setText] = useState(itemText(item));
  const [categoryId, setCategoryId] = useState(initialCategoryId || "");
  // A habit's direction follows the guess from its words until the user picks one; a choice saved before wins.
  const [chosen, setChosen] = useState(typeof item?.reduce === "boolean" ? item.reduce : null);
  const [frequency, setFrequency] = useState(item?.frequency || "");
  const goalOptions = goalChoices(goals, item?.goalId || initialGoalId);
  const [goalId, setGoalId] = useState(() => startingGoalId(goalOptions, item?.goalId, initialGoalId));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textRef = useRef(null);
  useEffect(() => { growToFit(textRef.current); }, []);

  const words = text.trim();
  const reduce = chosen ?? guessReduce(words);
  const repeat = words && (!categories || categoryId) && isTaken(words, categoryId);
  const canSave = !!words && (!categories || !!categoryId) && !repeat;
  const save = () => {
    if (!canSave) return;
    onSave(kind.arrows ? makeHabit({ text: words, reduce, frequency, goalId }) : words, categoryId);
    onClose();
  };
  const remove = () => { if (!confirmDelete) { setConfirmDelete(true); return; } onDelete(); onClose(); };

  return (
    <EditorFrame id="compass-editor-title" title={isNew ? kind.newTitle : kind.editTitle} canSave={canSave} onSave={save} onClose={onClose}>
      <div>
        <label htmlFor="compass-text" style={{ ...fieldLabel(), display: "block" }}>{kind.textLabel}</label>
        <textarea id="compass-text" ref={textRef} value={text} rows={2} autoFocus={isNew} placeholder={kind.placeholder}
          aria-describedby={repeat ? "compass-text-note" : undefined}
          onChange={e => { setText(e.target.value); growToFit(e.target); }}
          style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "12px 14px", fontSize: 16, color: C.text, fontFamily: "inherit", resize: "none", boxSizing: "border-box", lineHeight: 1.5, overflow: "hidden" }} />
        {repeat && <p id="compass-text-note" role="alert" style={{ margin: "8px 0 0", fontSize: 15, lineHeight: 1.5, color: C.red }}>{kind.repeat}</p>}
      </div>

      {kind.arrows && (
        <div>
          <div style={fieldLabel()}>Are you building it or cutting back?</div>
          <Segmented label="Are you building it or cutting back?" stretch value={reduce ? "cut" : "build"}
            options={[["build", "↑ Building it"], ["cut", "↓ Cutting it back"]]}
            onChange={(v) => setChosen(v === "cut")} />
          {chosen === null && words && (
            <p style={{ margin: "8px 0 0", fontSize: 15, lineHeight: 1.5, color: C.textMid }}>
              Nudge picked this from your words. Tap the other one if it's wrong.
            </p>
          )}
        </div>
      )}

      {categories && (
        <div>
          <label htmlFor="compass-category" style={{ ...fieldLabel(), display: "block" }}>Category</label>
          <select id="compass-category" value={categoryId} onChange={e => setCategoryId(e.target.value)}
            style={{ ...selectStyle(), color: categoryId ? C.text : C.textMid }}>
            <option value="" disabled>Choose a category</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{categoryLabel(cat)}</option>)}
          </select>
        </div>
      )}

      {kind.arrows && (
        <div>
          <label htmlFor="compass-frequency" style={{ ...fieldLabel(), display: "block" }}>How often</label>
          <select id="compass-frequency" value={frequency} onChange={e => setFrequency(e.target.value)} style={{ ...selectStyle(), color: frequency ? C.text : C.textMid }}>
            <option value="">Not set</option>
            {HABIT_FREQUENCIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>
      )}

      {kind.arrows && goalOptions.length > 0 && (
        <div>
          <label htmlFor="compass-goal" style={{ ...fieldLabel(), display: "block" }}>Goal</label>
          <GoalSelect id="compass-goal" options={goalOptions} value={goalId} onChange={setGoalId} style={selectStyle()} />
        </div>
      )}

      {!isNew && <DeleteButton noun={kind.one} confirming={confirmDelete} onClick={remove} />}
    </EditorFrame>
  );
}

